/**
 *
 * TaxonomyForm
 *
 */

import React from 'react';
import { Box, Container, Paper, Tab, Tabs } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useParams } from 'react-router';
import { useTaxonomiesSlice } from './slice';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectSaveTaxonomy,
  selectTaxonomy,
  selectUpdateTerms,
} from './slice/selectors';
import { useSnackbar } from 'notistack';
import { TaxonomyForm } from './Form';
import { Terms } from './Terms';
import { ChevronLeft } from '@mui/icons-material';
import { appActions } from '../App/slice';
import { useHasChanged } from '../../../utils/usePrevious';
import { grey } from '@mui/material/colors';

export function Edit() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { actions } = useTaxonomiesSlice();
  const loadTaxonomy = useSelector(selectTaxonomy);
  const saveTaxonomy = useSelector(selectSaveTaxonomy);
  const updateTerms = useSelector(selectUpdateTerms);

  const { enqueueSnackbar } = useSnackbar();

  const params = useParams<{
    id: string;
    subsection?: string;
  }>();

  // load()/save-response effects intentionally keyed to mount / route id /
  // request-loading transitions; load is recreated each render. Documented
  // disables preserve the original load triggers (see PreviewPages/Edit).
  React.useEffect(() => {
    load();
    return () => {
      dispatch(actions.loadTaxonomyUnmount());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (loadTaxonomy.error) {
      navigate('/portal/admin/taxonomies');
    }
  }, [loadTaxonomy, navigate]);

  React.useEffect(() => {
    console.log(saveTaxonomy);
    if (saveTaxonomy.loading) return;
    if (saveTaxonomy.error) {
      enqueueSnackbar(saveTaxonomy.error.message, {
        variant: 'error',
      });
    }
    if (saveTaxonomy.data) {
      enqueueSnackbar('Taxonomy saved successfully', {
        variant: 'success',
      });
      if (params.id === 'new') {
        navigate(`/portal/admin/taxonomies/${saveTaxonomy.data.id}`, {
          replace: true,
        });
      } else {
        load();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saveTaxonomy.loading]);

  React.useEffect(() => {
    if (params.id !== 'new') load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.id]);

  const updateLoadingHasChanged = useHasChanged(updateTerms.loading);

  React.useEffect(() => {
    if (updateLoadingHasChanged && !updateTerms.loading) {
      load();
      dispatch(appActions.loadRequest());
    }
  });

  const load = () => {
    const id = params.id === 'new' ? 'new' : parseInt(params.id || '');
    dispatch(actions.loadTaxonomyRequest({ id }));
  };

  return (
    <>
      <Box>
        <Container maxWidth={'xl'}>
          <Paper sx={{ my: 4 }}>
            <Box
              sx={{
                px: 1,
                backgroundColor: grey['200'],
              }}
            >
              <Tabs value={params.subsection || ''}>
                <Tab
                  value={''}
                  component={Link}
                  to={`/portal/admin/taxonomies/${params.id}`}
                  label={'General'}
                />
                <Tab
                  value={'terms'}
                  disabled={params.id === 'new'}
                  component={Link}
                  to={`/portal/admin/taxonomies/${params.id}/terms`}
                  label={'Terms'}
                />
                <Tab
                  icon={<ChevronLeft />}
                  iconPosition={'start'}
                  sx={{ marginLeft: 'auto' }}
                  component={Link}
                  to="/portal/admin/taxonomies"
                  label={'Back to taxonomies'}
                />
              </Tabs>
            </Box>

            {!params.subsection && (
              <TaxonomyForm
                loadTaxonomy={loadTaxonomy}
                saveTaxonomy={saveTaxonomy}
                onSave={data => dispatch(actions.saveTaxonomyRequest(data))}
              />
            )}
            {params.subsection === 'terms' && (
              <Terms
                updateTerms={updateTerms}
                loadTaxonomy={loadTaxonomy}
                onUpdate={data =>
                  dispatch(
                    actions.updateTermsRequest({
                      ...data,
                      id: loadTaxonomy.data?.id,
                    }),
                  )
                }
              />
            )}
          </Paper>
        </Container>
      </Box>
    </>
  );
}
